import { useEffect, useMemo, useState } from 'react';
import { CANCEL_JOB_ACTION } from '~/actions';
import {
  DashboardJobsType,
  useDashboardJobInfo,
  useGetDashboardJobs,
} from '~/domain/DashboardJobs';
import { useIsSuccess } from '~/hooks';

interface IUseJobListProps {
  jobType: DashboardJobsType;
}

export const useJobList = ({ jobType }: IUseJobListProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isFetching, isPending, isLoading, isRefetching, refetch } = useGetDashboardJobs({
    limit: 15,
    page: currentPage,
    type: jobType,
  });
  const info = useDashboardJobInfo();

  const { isSuccess: isCancelJobSuccess } = useIsSuccess([CANCEL_JOB_ACTION]);

  const totalPages = useMemo(() => {
    let counts = 0;

    switch (jobType) {
      case DashboardJobsType.ACTION_REQUIRED:
        counts = info?.pendingCount || 0;
        break;
      case DashboardJobsType.FILLED:
        counts = info?.filledCount || 0;
        break;
      case DashboardJobsType.OPEN:
        counts = info?.openCount || 0;
        break;
    }

    return Math.ceil(counts / 15);
  }, [info?.filledCount, info?.openCount, info?.pendingCount, jobType]);

  useEffect(() => {
    if (isCancelJobSuccess) {
      refetch();
    }
  }, [isCancelJobSuccess, refetch]);

  return {
    data,
    isInitialLoading: isLoading,
    isLoading: isLoading || isPending || isFetching || isRefetching,
    methods: {
      setCurrentPage,
    },
    pagination: {
      currentPage,
      totalPages,
    },
  };
};
